import React from "react";
import { Button } from "primereact/button";
import { AppCard } from "../../components/ui";
import { PreviewScreenProps } from "./types";
import { utils } from "../../utils/utils";

const PreviewScreen: React.FC<PreviewScreenProps> = ({
  transactions,
  totalAmount,
  hasErrors,
  onConfirmExecution,
  onCancel,
  onDownloadErrorLog,
  isProcessing = false,
  currentStep
}) => {
  return (
    <AppCard>
      <AppCard.Header title="Preview Transactions" />
      <AppCard.Body>
        {transactions.length > 0 ? (
          <div className="max-h-[500px] overflow-y-auto border border-gray-300 rounded-md bg-white relative mb-5">
            <table className="w-full text-sm table-fixed">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr>
                  <th style={{ width: '8%' }} className="!bg-gray-50 !border-b-2 !border-gray-300 font-semibold text-gray-600 py-3 px-2 text-left align-middle">#</th>
                  <th style={{ width: '15%' }} className="!bg-gray-50 !border-b-2 !border-gray-300 font-semibold text-gray-600 py-3 px-2 text-left align-middle">Recipient Name</th>
                  <th style={{ width: '20%' }} className="!bg-gray-50 !border-b-2 !border-gray-300 font-semibold text-gray-600 py-3 px-2 text-left align-middle">IBAN</th>
                  <th style={{ width: '10%' }} className="!bg-gray-50 !border-b-2 !border-gray-300 font-semibold text-gray-600 py-3 px-2 text-left align-middle">BIC</th>
                  <th style={{ width: '12%' }} className="!bg-gray-50 !border-b-2 !border-gray-300 font-semibold text-gray-600 py-3 px-2 text-left align-middle">Amount (BGN)</th>
                  <th style={{ width: '20%' }} className="!bg-gray-50 !border-b-2 !border-gray-300 font-semibold text-gray-600 py-3 px-2 text-left align-middle">Purpose</th>
                  <th style={{ width: '15%' }} className="!bg-gray-50 !border-b-2 !border-gray-300 font-semibold text-gray-600 py-3 px-2 text-left align-middle">Error</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className={!transaction.isValid || (transaction.errors && transaction.errors.length > 0) ? 'bg-red-50 border-l-4 border-l-red-500' : 'border-b border-gray-100 hover:bg-gray-50'}
                  >
                    <td style={{ width: '8%' }} className="py-2.5 px-2 align-middle text-left break-words"><strong>{transaction.id}</strong></td>
                    <td style={{ width: '15%' }} className="py-2.5 px-2 align-middle text-left break-words">{transaction.recipientName}</td>
                    <td style={{ width: '20%' }} className="py-2.5 px-2 align-middle text-left break-words">{transaction.iban}</td>
                    <td style={{ width: '10%' }} className="py-2.5 px-2 align-middle text-left break-words">{transaction.bic}</td>
                    <td style={{ width: '12%' }} className="py-2.5 px-2 align-middle text-left break-words">{utils.formatCurrency(transaction.amount, 'BGN')}</td>
                    <td style={{ width: '20%' }} className="py-2.5 px-2 align-middle text-left break-words">{transaction.purpose}</td>
                    <td style={{ width: '15%' }} className="py-2.5 px-2 align-middle text-left break-words">
                      {transaction.errors && transaction.errors.length > 0 ? (
                        <div>
                          {transaction.errors.map((error, errorIndex) => (
                            <div key={errorIndex} className="flex items-center mb-1 text-[0.85rem] last:mb-0">
                              <i className="fas fa-exclamation-triangle text-danger-600 mr-2 text-[0.8rem] flex-shrink-0"></i>
                              {error}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-success-600">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-neutral-500">No transactions to preview. Please upload a file first.</p>
          </div>
        )}

        <div className="mt-3">
          <p><strong>Total: {utils.formatCurrency(totalAmount, 'BGN')}, {transactions.length} transactions</strong></p>
        </div>

        {hasErrors && (
          <div className="mt-3 mb-3">
            <AppCard className="border-2 border-red-500 bg-red-100">
              <AppCard.Body>
                <div className="flex items-center">
                  <i className="fas fa-exclamation-triangle text-2xl text-red-500 mr-3"></i>
                  <div>
                    <h6 className="font-bold text-red-500 mb-1">
                      Errors Detected
                    </h6>
                    <p className="text-red-500 mb-2">
                      {transactions.filter(t => !t.isValid || (t.errors && t.errors.length > 0)).length} transaction(s) have errors that need attention.
                      <br />
                      <small className="text-neutral-500">
                        <i className="fas fa-info-circle mr-1"></i>
                        This file has been automatically added to the History tab for review.
                      </small>
                    </p>
                    <Button
                      severity="danger"
                      onClick={onDownloadErrorLog}
                      size="small"
                      className="rounded-full font-bold"
                      label="Download Error Log (TXT)"
                      icon="fas fa-download"
                    />
                  </div>
                </div>
              </AppCard.Body>
            </AppCard>
          </div>
        )}

        {!isProcessing && currentStep !== 'logs' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Button
                onClick={onConfirmExecution}
                disabled={hasErrors}
                className="w-full rounded px-6 py-3 text-sm font-medium transition-all border border-transparent h-11 inline-flex items-center justify-center min-w-[160px] bg-green-600 text-white border-green-600 hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed disabled:opacity-60"
                label="Confirm Execution"
                icon="fas fa-check"
              />
            </div>
            <div>
              <Button
                onClick={onCancel}
                severity="danger"
                className="w-full rounded px-6 py-3 text-sm font-medium transition-all h-11 inline-flex items-center justify-center min-w-[120px]"
                label="Cancel"
                icon="fas fa-times"
              />
            </div>
          </div>
        )}
      </AppCard.Body>
    </AppCard>
  );
};

export default PreviewScreen;
